/**
 * The words of invoicing's sales tools, closed together: the item catalogue that fills a line
 * (#81), receipt and payment vouchers that allocate across open documents (#85), quotations that
 * become invoices (#97), and copying, recurring invoices and keyboard shortcuts (#93). Kept beside
 * the catalogue, as the hubs' and views' words are, and spread into it.
 */
export const INVOICING_SALES_EN = {
    // #81 — items.
    'nav.invoicing.items': 'Items',
    'screen.invoicing.items.title': 'Items',
    'screen.invoicing.items.lede': 'The products and services a line starts from: choosing one fills its description, price, tax code and account, all still editable.',
    'screen.invoicing.items.column.code': 'Code',
    'screen.invoicing.items.column.name_ar': 'Name',
    'screen.invoicing.items.column.name_en': 'Name (English)',
    'screen.invoicing.items.column.item_type': 'Type',
    'screen.invoicing.items.column.unit': 'Unit',
    'screen.invoicing.items.column.sales_price': 'Sales price',
    'screen.invoicing.items.column.sales_tax_code': 'Sales tax',
    'screen.invoicing.items.column.income_account_name': 'Income account',
    'screen.invoicing.items.column.purchase_price': 'Purchase price',
    'screen.invoicing.items.column.purchase_tax_code': 'Purchase tax',
    'screen.invoicing.items.column.expense_account_name': 'Expense account',
    'screen.invoicing.items.column.active': 'In use',
    'screen.invoicing.items.column.id': 'Id',
    'screen.invoicing.items.stat.rows': 'Items',
    'screen.invoicing.items.section.item': 'The item',
    'screen.invoicing.items.section.sales': 'When it is sold',
    'screen.invoicing.items.section.purchase': 'When it is bought',
    'screen.invoicing.items.action.create': 'New item',
    'screen.invoicing.items.action.edit': 'Edit',
    'screen.invoicing.items.action.retire': 'Retire',
    'value.item_type.goods': 'Goods',
    'value.item_type.service': 'Service',
    'editor.line.item': 'Item',
    'editor.choices.items': 'the items',
    'change.field.item.code': 'Code',
    'change.field.item.name_ar': 'Name (Arabic)',
    'change.field.item.name_en': 'Name (English)',
    'change.field.item.item_type': 'Type',
    'change.field.item.unit': 'Unit',
    'change.field.item.sales_price': 'Sales price',
    'change.field.item.sales_tax_code_id': 'Sales tax code',
    'change.field.item.income_account_id': 'Income account',
    'change.field.item.purchase_price': 'Purchase price',
    'change.field.item.purchase_tax_code_id': 'Purchase tax code',
    'change.field.item.expense_account_id': 'Expense account',
    'change.field.item.retired_at': 'Retired on',
    'change.field.document.item_id': 'Item',
    ...fieldWords('en'),
    // #85 — receipt and payment vouchers.
    'screen.invoicing.payment_new.title': 'Receipt or payment voucher',
    'screen.invoicing.payment_new.lede': 'The party, the amount, and the open documents it settles. What is not allocated stays on the voucher as an advance.',
    'screen.invoicing.payments.action.new_voucher': 'New voucher',
    'screen.invoicing.documents.action.register_payment': 'Register payment',
    'screen.invoicing.vendor_bills.action.register_payment': 'Register payment',
    'voucher.title.inbound': 'Receipt voucher',
    'voucher.title.outbound': 'Payment voucher',
    'voucher.lede.inbound': 'Money received from a customer, set against what they owe.',
    'voucher.lede.outbound': 'Money paid to a vendor, set against what we owe them.',
    'voucher.start.failed': 'The voucher could not start from that document: {reason}',
    'voucher.start.nothingOpen': 'nothing is left to pay on it',
    'voucher.field.direction': 'Voucher',
    'voucher.field.payer': 'Received from',
    'voucher.field.payee': 'Paid to',
    'voucher.field.paidOn': 'Date',
    'voucher.field.method': 'Method',
    'voucher.field.journal': 'Bank or cash journal',
    'voucher.field.journal.hint': 'Its default account is where the money is.',
    'voucher.field.reference': 'Bank reference',
    'voucher.field.reference.hint': 'The same reference is never recorded twice.',
    'voucher.field.amountIn': 'Amount received',
    'voucher.field.amountOut': 'Amount paid',
    'voucher.currency': 'SAR',
    'voucher.payAllOpen': 'Everything open: SAR {amount}',
    'voucher.documents.title': 'Open documents',
    'voucher.documents.lede.inbound': "The customer's invoices with something left to pay, the oldest due first.",
    'voucher.documents.lede.outbound': "The vendor's bills with something left to pay, the oldest due first.",
    'voucher.documents.noParty': 'Choose the party to see what is open.',
    'voucher.documents.none.inbound': 'This customer has no invoice left to pay. The whole amount stays on the voucher as an advance.',
    'voucher.documents.none.outbound': 'Nothing is owed to this vendor. The whole amount stays on the voucher as an advance.',
    'voucher.documents.overdue': 'overdue',
    'voucher.documents.due': 'due {due} · open SAR {open}',
    'voucher.documents.amount': 'Amount on {number}',
    'voucher.payInFull': 'Pay in full',
    'voucher.auto': 'Allocate oldest first',
    'voucher.clear': 'Clear',
    'voucher.sums': 'Received SAR {received} · allocated SAR {allocated} · left over SAR {excess}',
    'voucher.sums.advance': 'What is left over stays on the voucher as an advance, to allocate later.',
    'voucher.need.party': 'the party',
    'voucher.need.journal': 'a bank or cash journal',
    'voucher.need.amount': 'an amount',
    'voucher.need.over': 'allocations within the amount',
    'voucher.need.overDocument': 'no more on {number} than is open',
    'voucher.need.writeOff': 'the write-off document, account and reason',
    'voucher.writeOff.title': 'A difference left on a document',
    'voucher.writeOff.lede': 'Only when you choose it: what this voucher leaves open on one document is booked to an account, with a reason, in the voucher itself.',
    'voucher.writeOff.ask': 'Write off the difference',
    'voucher.writeOff.document': 'Document',
    'voucher.writeOff.option': '{number} — SAR {amount} left',
    'voucher.writeOff.account': 'Account',
    'voucher.writeOff.account.hint.inbound': 'An expense account, such as discounts allowed.',
    'voucher.writeOff.account.hint.outbound': 'An income account, such as discounts received.',
    'voucher.writeOff.reason': 'Reason',
    'voucher.writeOff.says': 'SAR {amount} on {number} will be written off, and it will be settled.',
    'voucher.save.inbound': 'Save receipt voucher',
    'voucher.save.outbound': 'Save payment voucher',
    'voucher.saved': 'Saved and posted. Every riyal of it is allocated.',
    'voucher.saved.advance': 'Saved and posted. SAR {excess} is not allocated and stays on the voucher as an advance.',
    'change.field.payment.account_id': 'Write-off account',
    'change.field.payment.reason': 'Write-off reason',
    // #97 — quotations.
    'nav.invoicing.quotations': 'Quotations',
    'screen.invoicing.quotations.title': 'Quotations',
    'screen.invoicing.quotations.lede': 'Offers to a customer before an invoice: numbered when sent, valid until they expire, and converted once into an invoice draft.',
    'screen.invoicing.quotations.column.number': 'Number',
    'screen.invoicing.quotations.column.party_name': 'Customer',
    'screen.invoicing.quotations.column.issue_on': 'Date',
    'screen.invoicing.quotations.column.expires_on': 'Expires on',
    'screen.invoicing.quotations.column.total': 'Total',
    'screen.invoicing.quotations.column.standing': 'State',
    'screen.invoicing.quotations.column.invoice_number': 'Invoice',
    'screen.invoicing.quotations.column.party_id': 'Customer',
    'screen.invoicing.quotations.column.invoice_id': 'Invoice id',
    'screen.invoicing.quotations.column.id': 'Id',
    'screen.invoicing.quotations.action.create': 'New quotation',
    'screen.invoicing.quotations.action.edit': 'Edit draft',
    'screen.invoicing.quotations.action.send': 'Send',
    'screen.invoicing.quotations.action.accept': 'Accepted by the customer',
    'screen.invoicing.quotations.action.convert': 'Convert to invoice',
    'screen.invoicing.quotation_new.title': 'Quotation',
    'screen.invoicing.quotation_new.lede': 'Customer, lines and the day it expires. Save keeps a draft; send numbers it.',
    'value.standing.draft': 'Draft',
    'value.standing.sent': 'Sent',
    'value.standing.accepted': 'Accepted',
    'value.standing.expired': 'Expired',
    'value.standing.invoiced': 'Invoiced',
    'quotation.field.expiresOn': 'Expires on',
    'quotation.field.expiresOn.hint': 'The last day the prices hold. Required to send it.',
    'quotation.need.expiresOn': 'the day it expires',
    'quotation.saveSend': 'Save and send',
    'quotation.sent': 'Sent as {id}. Nothing was posted; convert it to an invoice once the customer agrees.',
    'quotation.held': 'Saved as a draft, not sent: {reason}',
    'editor.draft.status.sent': 'sent',
    'editor.draft.status.accepted': 'accepted',
    'editor.draft.status.invoiced': 'invoiced',
    'change.field.document.expires_on': 'Expires on',
    'change.field.document.quotation_id': 'Quotation',
    // #93 — copy.
    'screen.invoicing.documents.action.copy': 'Copy',
    'screen.invoicing.vendor_bills.action.copy': 'Copy',
    'screen.accounting.entries.action.copy': 'Copy',
    'screen.invoicing.invoice_copy.title': 'Copy of an invoice',
    'screen.invoicing.invoice_copy.lede': 'A new draft with the lines of the invoice copied, dated today, with no number. Nothing is saved until you save it.',
    'screen.invoicing.vendor_bill_copy.title': 'Copy of a vendor bill',
    'screen.invoicing.vendor_bill_copy.lede': 'A new draft with the lines of the bill copied, dated today, with no number. Nothing is saved until you save it.',
    'screen.accounting.entry_copy.title': 'Copy of an entry',
    'screen.accounting.entry_copy.lede': 'A new entry with the journal, narration and lines copied, dated today. Nothing is posted until you post it.',
    'copy.notice.document': 'A copy: dated today, with no number and no due day. It is saved as a new draft only when you save it; the original is not touched.',
    'copy.notice.entry': 'A copy: dated today. It is posted as a new entry only when you post it; the original is not touched.',
    'copy.failed': 'The copy could not be read: {reason}',
    // #93 — keyboard shortcuts.
    'hotkeys.title': 'Keyboard shortcuts',
    'hotkeys.single': 'One-letter shortcuts (such as N)',
    'hotkeys.lede': 'What the keys do on this page. A single key is not taken while you type in a field; one with ⌘, Ctrl or Alt is.',
    'hotkeys.palette': 'Search and go anywhere',
    'hotkeys.sidebar': 'Show or hide the sidebar',
    'hotkeys.sheet': 'This list',
    // #93 — recurring invoices.
    'nav.invoicing.recurring': 'Recurring invoices',
    'screen.invoicing.recurring.title': 'Recurring invoices',
    'screen.invoicing.recurring.lede': 'Invoices repeated on a schedule; every run makes a draft for a person to issue, and a run that failed is counted here with its reason.',
    'screen.invoicing.recurring.column.name': 'Name',
    'screen.invoicing.recurring.column.party_name': 'Customer',
    'screen.invoicing.recurring.column.frequency': 'Every',
    'screen.invoicing.recurring.column.next_run_on': 'Next run',
    'screen.invoicing.recurring.column.starts_on': 'From',
    'screen.invoicing.recurring.column.ends_on': 'Until',
    'screen.invoicing.recurring.column.state': 'State',
    'screen.invoicing.recurring.column.drafted_count': 'Drafts made',
    'screen.invoicing.recurring.column.failed_count': 'Failed runs',
    'screen.invoicing.recurring.column.last_error': 'Last failure',
    'screen.invoicing.recurring.column.party_id': 'Customer',
    'screen.invoicing.recurring.column.id': 'Id',
    'screen.invoicing.recurring.action.run': 'Draft what is due now',
    'screen.invoicing.recurring.action.pause': 'Pause',
    'screen.invoicing.recurring.action.resume': 'Resume',
    'screen.invoicing.recurring.action.end': 'End',
    'screen.invoicing.documents.action.make_recurring': 'Make recurring',
    'screen.invoicing.documents.action.make_recurring.field.frequency': 'Every',
    'screen.invoicing.documents.action.make_recurring.field.starts_on': 'First invoice on',
    'screen.invoicing.documents.action.make_recurring.field.starts_on.help': 'Each run makes a draft on its day; a person issues it.',
    'screen.invoicing.documents.action.make_recurring.field.ends_on': 'Last invoice by',
    'screen.invoicing.documents.action.make_recurring.field.ends_on.help': 'Empty: until it is ended.',
    'screen.invoicing.documents.action.make_recurring.field.name': 'Name',
    'screen.invoicing.documents.action.make_recurring.field.name.help': "Empty: the customer and the invoice's number.",
    'value.frequency.monthly': 'Monthly',
    'value.frequency.quarterly': 'Quarterly',
    'value.frequency.yearly': 'Yearly',
    'value.state.active': 'Active',
    'value.state.paused': 'Paused',
    'value.state.ended': 'Ended',
    'change.field.recurring_profile.name': 'Name',
    'change.field.recurring_profile.source_document_id': 'Made from',
    'change.field.recurring_profile.party_id': 'Customer',
    'change.field.recurring_profile.party_snapshot': 'Customer as invoiced',
    'change.field.recurring_profile.journal_id': 'Journal',
    'change.field.recurring_profile.branch_code': 'Branch',
    'change.field.recurring_profile.currency': 'Currency',
    'change.field.recurring_profile.lines': 'Lines',
    'change.field.recurring_profile.frequency': 'Every',
    'change.field.recurring_profile.starts_on': 'From',
    'change.field.recurring_profile.ends_on': 'Until',
    'change.field.recurring_profile.next_run_on': 'Next run',
    'change.field.recurring_profile.state': 'State',
    'change.field.recurring_profile.created_by': 'Created by',
    'change.field.recurring_profile.id': 'Run',
    'change.field.recurring_profile.period_on': 'Run day',
    'change.field.recurring_profile.outcome': 'Outcome',
    'change.field.recurring_profile.document_id': 'Draft made',
    'change.field.recurring_profile.error': 'Why it failed',
    'change.field.recurring_profile.prices_include_vat': 'Prices include VAT',
    'change.field.recurring_profile.document_discount': 'Document discount',
    // ── list pages: what an empty list means (list-standard §11, R6) ──
    'screen.invoicing.items.empty': 'No items yet',
    'screen.invoicing.items.empty.description': 'An item fills a line in one choice; without one, every line is typed by hand.',
    'screen.invoicing.quotations.empty': 'No quotations yet',
    'screen.invoicing.quotations.empty.description': 'Each quotation appears here with its customer, expiry and total until it is invoiced or expires.',
};
export const INVOICING_SALES_AR = {
    // #81 — الأصناف.
    'nav.invoicing.items': 'الأصناف',
    'screen.invoicing.items.title': 'الأصناف',
    'screen.invoicing.items.lede': 'المنتجات والخدمات التي يبدأ منها سطر الفاتورة: اختيار الصنف يملأ وصفه وسعره ورمز ضريبته وحسابه، ويبقى كل ذلك قابلًا للتعديل.',
    'screen.invoicing.items.column.code': 'الرمز',
    'screen.invoicing.items.column.name_ar': 'الاسم',
    'screen.invoicing.items.column.name_en': 'الاسم بالإنجليزية',
    'screen.invoicing.items.column.item_type': 'النوع',
    'screen.invoicing.items.column.unit': 'الوحدة',
    'screen.invoicing.items.column.sales_price': 'سعر البيع',
    'screen.invoicing.items.column.sales_tax_code': 'ضريبة البيع',
    'screen.invoicing.items.column.income_account_name': 'حساب الإيراد',
    'screen.invoicing.items.column.purchase_price': 'سعر الشراء',
    'screen.invoicing.items.column.purchase_tax_code': 'ضريبة الشراء',
    'screen.invoicing.items.column.expense_account_name': 'حساب المصروف',
    'screen.invoicing.items.column.active': 'قيد الاستعمال',
    'screen.invoicing.items.column.id': 'المعرّف',
    'screen.invoicing.items.stat.rows': 'الأصناف',
    'screen.invoicing.items.section.item': 'الصنف',
    'screen.invoicing.items.section.sales': 'عند البيع',
    'screen.invoicing.items.section.purchase': 'عند الشراء',
    'screen.invoicing.items.action.create': 'صنف جديد',
    'screen.invoicing.items.action.edit': 'تعديل',
    'screen.invoicing.items.action.retire': 'إيقاف',
    'value.item_type.goods': 'سلعة',
    'value.item_type.service': 'خدمة',
    'editor.line.item': 'الصنف',
    'editor.choices.items': 'الأصناف',
    'change.field.item.code': 'الرمز',
    'change.field.item.name_ar': 'الاسم بالعربية',
    'change.field.item.name_en': 'الاسم بالإنجليزية',
    'change.field.item.item_type': 'النوع',
    'change.field.item.unit': 'الوحدة',
    'change.field.item.sales_price': 'سعر البيع',
    'change.field.item.sales_tax_code_id': 'رمز ضريبة البيع',
    'change.field.item.income_account_id': 'حساب الإيراد',
    'change.field.item.purchase_price': 'سعر الشراء',
    'change.field.item.purchase_tax_code_id': 'رمز ضريبة الشراء',
    'change.field.item.expense_account_id': 'حساب المصروف',
    'change.field.item.retired_at': 'تاريخ الإيقاف',
    'change.field.document.item_id': 'الصنف',
    ...fieldWords('ar'),
    // #85 — سند القبض وسند الصرف.
    'screen.invoicing.payment_new.title': 'سند قبض أو صرف',
    'screen.invoicing.payment_new.lede': 'الطرف والمبلغ والمستندات المفتوحة التي يسددها. ما لا يُخصَّص يبقى على السند دفعةً مقدّمة.',
    'screen.invoicing.payments.action.new_voucher': 'سند جديد',
    'screen.invoicing.documents.action.register_payment': 'تسجيل سداد',
    'screen.invoicing.vendor_bills.action.register_payment': 'تسجيل سداد',
    'voucher.title.inbound': 'سند قبض',
    'voucher.title.outbound': 'سند صرف',
    'voucher.lede.inbound': 'مبلغ مقبوض من عميل، يُخصَّص على ما عليه.',
    'voucher.lede.outbound': 'مبلغ مدفوع لمورد، يُخصَّص على ما له علينا.',
    'voucher.start.failed': 'تعذّر بدء السند من ذلك المستند: {reason}',
    'voucher.start.nothingOpen': 'لم يبقَ عليه شيء للسداد',
    'voucher.field.direction': 'السند',
    'voucher.field.payer': 'مقبوض من',
    'voucher.field.payee': 'مدفوع إلى',
    'voucher.field.paidOn': 'التاريخ',
    'voucher.field.method': 'طريقة الدفع',
    'voucher.field.journal': 'دفتر البنك أو الصندوق',
    'voucher.field.journal.hint': 'حسابه الافتراضي هو مكان المال.',
    'voucher.field.reference': 'مرجع البنك',
    'voucher.field.reference.hint': 'لا يُسجَّل المرجع نفسه مرتين.',
    'voucher.field.amountIn': 'المبلغ المقبوض',
    'voucher.field.amountOut': 'المبلغ المدفوع',
    'voucher.currency': 'ر.س',
    'voucher.payAllOpen': 'كل المفتوح: {amount} ر.س',
    'voucher.documents.title': 'المستندات المفتوحة',
    'voucher.documents.lede.inbound': 'فواتير العميل التي بقي عليها شيء، الأقدم استحقاقًا أولًا.',
    'voucher.documents.lede.outbound': 'فواتير المورد التي بقي عليها شيء، الأقدم استحقاقًا أولًا.',
    'voucher.documents.noParty': 'اختر الطرف لترى ما هو مفتوح.',
    'voucher.documents.none.inbound': 'لا فاتورة مفتوحة على هذا العميل. يبقى المبلغ كله على السند دفعةً مقدّمة.',
    'voucher.documents.none.outbound': 'لا شيء مستحق لهذا المورد. يبقى المبلغ كله على السند دفعةً مقدّمة.',
    'voucher.documents.overdue': 'متأخرة',
    'voucher.documents.due': 'تستحق {due} · المفتوح {open} ر.س',
    'voucher.documents.amount': 'المبلغ على {number}',
    'voucher.payInFull': 'سداد كامل',
    'voucher.auto': 'التخصيص للأقدم أولًا',
    'voucher.clear': 'مسح',
    'voucher.sums': 'المقبوض {received} ر.س · المخصَّص {allocated} ر.س · المتبقي {excess} ر.س',
    'voucher.sums.advance': 'المتبقي يبقى على السند دفعةً مقدّمة، يُخصَّص لاحقًا.',
    'voucher.need.party': 'الطرف',
    'voucher.need.journal': 'دفتر بنك أو صندوق',
    'voucher.need.amount': 'المبلغ',
    'voucher.need.over': 'تخصيص لا يتجاوز المبلغ',
    'voucher.need.overDocument': 'ألّا يتجاوز المخصَّص على {number} ما هو مفتوح',
    'voucher.need.writeOff': 'مستند الشطب وحسابه وسببه',
    'voucher.writeOff.title': 'فرق باقٍ على مستند',
    'voucher.writeOff.lede': 'باختيارك فقط: ما يتركه هذا السند مفتوحًا على مستند واحد يُقيَّد على حساب، مع السبب، داخل السند نفسه.',
    'voucher.writeOff.ask': 'شطب الفرق',
    'voucher.writeOff.document': 'المستند',
    'voucher.writeOff.option': '{number} — باقٍ {amount} ر.س',
    'voucher.writeOff.account': 'الحساب',
    'voucher.writeOff.account.hint.inbound': 'حساب مصروف، مثل الخصم المسموح به.',
    'voucher.writeOff.account.hint.outbound': 'حساب إيراد، مثل الخصم المكتسب.',
    'voucher.writeOff.reason': 'السبب',
    'voucher.writeOff.says': 'سيُشطب {amount} ر.س من {number} ويصبح مسدَّدًا.',
    'voucher.save.inbound': 'حفظ سند القبض',
    'voucher.save.outbound': 'حفظ سند الصرف',
    'voucher.saved': 'حُفظ ورُحِّل، وخُصِّص كاملًا.',
    'voucher.saved.advance': 'حُفظ ورُحِّل. {excess} ر.س غير مخصَّصة وتبقى على السند دفعةً مقدّمة.',
    'change.field.payment.account_id': 'حساب الشطب',
    'change.field.payment.reason': 'سبب الشطب',
    // #97 — عروض الأسعار.
    'nav.invoicing.quotations': 'عروض الأسعار',
    'screen.invoicing.quotations.title': 'عروض الأسعار',
    'screen.invoicing.quotations.lede': 'عروض للعميل قبل الفاتورة: يُرقَّم العرض عند إرساله، ويسري حتى انتهائه، ويتحوّل مرة واحدة إلى مسودة فاتورة.',
    'screen.invoicing.quotations.column.number': 'الرقم',
    'screen.invoicing.quotations.column.party_name': 'العميل',
    'screen.invoicing.quotations.column.issue_on': 'التاريخ',
    'screen.invoicing.quotations.column.expires_on': 'ينتهي في',
    'screen.invoicing.quotations.column.total': 'الإجمالي',
    'screen.invoicing.quotations.column.standing': 'الحالة',
    'screen.invoicing.quotations.column.invoice_number': 'الفاتورة',
    'screen.invoicing.quotations.column.party_id': 'العميل',
    'screen.invoicing.quotations.column.invoice_id': 'معرّف الفاتورة',
    'screen.invoicing.quotations.column.id': 'المعرّف',
    'screen.invoicing.quotations.action.create': 'عرض سعر جديد',
    'screen.invoicing.quotations.action.edit': 'تعديل المسودة',
    'screen.invoicing.quotations.action.send': 'إرسال',
    'screen.invoicing.quotations.action.accept': 'قبِله العميل',
    'screen.invoicing.quotations.action.convert': 'تحويل إلى فاتورة',
    'screen.invoicing.quotation_new.title': 'عرض سعر',
    'screen.invoicing.quotation_new.lede': 'العميل والأسطر وتاريخ الانتهاء. الحفظ يبقيه مسودة، والإرسال يرقّمه.',
    'value.standing.draft': 'مسودة',
    'value.standing.sent': 'مُرسَل',
    'value.standing.accepted': 'مقبول',
    'value.standing.expired': 'منتهٍ',
    'value.standing.invoiced': 'مُفوتَر',
    'quotation.field.expiresOn': 'ينتهي في',
    'quotation.field.expiresOn.hint': 'آخر يوم تسري فيه الأسعار. مطلوب للإرسال.',
    'quotation.need.expiresOn': 'تاريخ الانتهاء',
    'quotation.saveSend': 'حفظ وإرسال',
    'quotation.sent': 'أُرسل برقم {id}. لم يُرحَّل شيء؛ حوّله إلى فاتورة متى وافق العميل.',
    'quotation.held': 'حُفظ مسودةً ولم يُرسَل: {reason}',
    'editor.draft.status.sent': 'مُرسَل',
    'editor.draft.status.accepted': 'مقبول',
    'editor.draft.status.invoiced': 'مُفوتَر',
    'change.field.document.expires_on': 'ينتهي في',
    'change.field.document.quotation_id': 'عرض السعر',
    // #93 — النسخ.
    'screen.invoicing.documents.action.copy': 'نسخ',
    'screen.invoicing.vendor_bills.action.copy': 'نسخ',
    'screen.accounting.entries.action.copy': 'نسخ',
    'screen.invoicing.invoice_copy.title': 'نسخة من فاتورة',
    'screen.invoicing.invoice_copy.lede': 'مسودة جديدة بأسطر الفاتورة المنسوخة، بتاريخ اليوم وبلا رقم. لا يُحفظ شيء حتى تحفظ.',
    'screen.invoicing.vendor_bill_copy.title': 'نسخة من فاتورة مورد',
    'screen.invoicing.vendor_bill_copy.lede': 'مسودة جديدة بأسطر الفاتورة المنسوخة، بتاريخ اليوم وبلا رقم. لا يُحفظ شيء حتى تحفظ.',
    'screen.accounting.entry_copy.title': 'نسخة من قيد',
    'screen.accounting.entry_copy.lede': 'قيد جديد بدفتر القيد المنسوخ وبيانه وأسطره، بتاريخ اليوم. لا يُرحَّل شيء حتى ترحّله.',
    'copy.notice.document': 'نسخة: بتاريخ اليوم، بلا رقم ولا تاريخ استحقاق. تُحفظ مسودةً جديدة عند الحفظ فقط، ولا يُمسّ الأصل.',
    'copy.notice.entry': 'نسخة: بتاريخ اليوم. تُرحَّل قيدًا جديدًا عند الترحيل فقط، ولا يُمسّ الأصل.',
    'copy.failed': 'تعذّرت قراءة المنسوخ: {reason}',
    // #93 — اختصارات لوحة المفاتيح.
    'hotkeys.title': 'اختصارات لوحة المفاتيح',
    'hotkeys.single': 'اختصارات الحرف الواحد (مثل N)',
    'hotkeys.lede': 'ما تفعله المفاتيح في هذه الصفحة. المفتاح المفرد لا يُؤخذ أثناء الكتابة في حقل، أمّا مع ⌘ أو Ctrl أو Alt فيُؤخذ.',
    'hotkeys.palette': 'البحث والانتقال إلى أي مكان',
    'hotkeys.sidebar': 'إظهار الشريط الجانبي أو إخفاؤه',
    'hotkeys.sheet': 'هذه القائمة',
    // #93 — الفواتير المتكررة.
    'nav.invoicing.recurring': 'الفواتير المتكررة',
    'screen.invoicing.recurring.title': 'الفواتير المتكررة',
    'screen.invoicing.recurring.lede': 'فواتير تتكرر بجدول؛ كل دورة تُنشئ مسودة يُصدرها شخص، والدورة التي تعذّرت تُعدّ هنا مع سببها.',
    'screen.invoicing.recurring.column.name': 'الاسم',
    'screen.invoicing.recurring.column.party_name': 'العميل',
    'screen.invoicing.recurring.column.frequency': 'كل',
    'screen.invoicing.recurring.column.next_run_on': 'الدورة القادمة',
    'screen.invoicing.recurring.column.starts_on': 'من',
    'screen.invoicing.recurring.column.ends_on': 'حتى',
    'screen.invoicing.recurring.column.state': 'الحالة',
    'screen.invoicing.recurring.column.drafted_count': 'المسودات المُنشأة',
    'screen.invoicing.recurring.column.failed_count': 'دورات متعثرة',
    'screen.invoicing.recurring.column.last_error': 'آخر تعثّر',
    'screen.invoicing.recurring.column.party_id': 'العميل',
    'screen.invoicing.recurring.column.id': 'المعرّف',
    'screen.invoicing.recurring.action.run': 'إنشاء المستحق الآن',
    'screen.invoicing.recurring.action.pause': 'إيقاف مؤقت',
    'screen.invoicing.recurring.action.resume': 'استئناف',
    'screen.invoicing.recurring.action.end': 'إنهاء',
    'screen.invoicing.documents.action.make_recurring': 'جعلها متكررة',
    'screen.invoicing.documents.action.make_recurring.field.frequency': 'كل',
    'screen.invoicing.documents.action.make_recurring.field.starts_on': 'أول فاتورة في',
    'screen.invoicing.documents.action.make_recurring.field.starts_on.help': 'كل دورة تُنشئ مسودة في يومها، ويُصدرها شخص.',
    'screen.invoicing.documents.action.make_recurring.field.ends_on': 'آخر فاتورة بحلول',
    'screen.invoicing.documents.action.make_recurring.field.ends_on.help': 'فارغ: حتى تُنهى.',
    'screen.invoicing.documents.action.make_recurring.field.name': 'الاسم',
    'screen.invoicing.documents.action.make_recurring.field.name.help': 'فارغ: العميل ورقم الفاتورة.',
    'value.frequency.monthly': 'شهريًا',
    'value.frequency.quarterly': 'ربع سنوي',
    'value.frequency.yearly': 'سنويًا',
    'value.state.active': 'نشطة',
    'value.state.paused': 'موقوفة مؤقتًا',
    'value.state.ended': 'منتهية',
    'change.field.recurring_profile.name': 'الاسم',
    'change.field.recurring_profile.source_document_id': 'مأخوذة من',
    'change.field.recurring_profile.party_id': 'العميل',
    'change.field.recurring_profile.party_snapshot': 'العميل كما فُوتر',
    'change.field.recurring_profile.journal_id': 'الدفتر',
    'change.field.recurring_profile.branch_code': 'الفرع',
    'change.field.recurring_profile.currency': 'العملة',
    'change.field.recurring_profile.lines': 'الأسطر',
    'change.field.recurring_profile.frequency': 'كل',
    'change.field.recurring_profile.starts_on': 'من',
    'change.field.recurring_profile.ends_on': 'حتى',
    'change.field.recurring_profile.next_run_on': 'الدورة القادمة',
    'change.field.recurring_profile.state': 'الحالة',
    'change.field.recurring_profile.created_by': 'أنشأها',
    'change.field.recurring_profile.id': 'الدورة',
    'change.field.recurring_profile.period_on': 'يوم الدورة',
    'change.field.recurring_profile.outcome': 'النتيجة',
    'change.field.recurring_profile.document_id': 'المسودة المُنشأة',
    'change.field.recurring_profile.error': 'سبب التعثّر',
    'change.field.recurring_profile.prices_include_vat': 'الأسعار شاملة الضريبة',
    'change.field.recurring_profile.document_discount': 'خصم المستند',
    // ── list pages: what an empty list means (list-standard §11, R6) ──
    'screen.invoicing.items.empty': 'لا أصناف بعد',
    'screen.invoicing.items.empty.description': 'الصنف يملأ السطر باختيار واحد، ومن دونه يُكتب كل سطر يدويًا.',
    'screen.invoicing.quotations.empty': 'لا عروض أسعار بعد',
    'screen.invoicing.quotations.empty.description': 'كل عرض سعر يظهر هنا بعميله وتاريخ انتهائه وإجماليه حتى يُفوتَر أو ينتهي.',
};
/**
 * The item form's fields, the same words in its create and its edit — written once, since a form
 * that calls a field one thing when it is made and another when it is changed is two products.
 */
function fieldWords(locale) {
    const words = {
        name_ar: ['Name (Arabic)', 'الاسم بالعربية'],
        'name_ar.help': ['What the invoice line says. The authority reads the item in Arabic.', 'ما يقوله سطر الفاتورة. تقرأ الهيئة الصنف بالعربية.'],
        name_en: ['Name (English)', 'الاسم بالإنجليزية'],
        code: ['Code', 'الرمز'],
        'code.help': ['Optional. Two items in use never share a code.', 'اختياري. لا يشترك صنفان قيد الاستعمال في رمز واحد.'],
        item_type: ['Type', 'النوع'],
        unit: ['Unit', 'الوحدة'],
        'unit.help': ['How it is counted on a line: hour, piece, month.', 'كيف يُعدّ على السطر: ساعة، قطعة، شهر.'],
        sales_price: ['Sales price', 'سعر البيع'],
        sales_tax_code_id: ['Sales tax code', 'رمز ضريبة البيع'],
        income_account_id: ['Income account', 'حساب الإيراد'],
        'income_account_id.help': ['An income account. A line on an invoice is booked to it.', 'حساب إيراد يُقيَّد عليه سطر الفاتورة.'],
        purchase_price: ['Purchase price', 'سعر الشراء'],
        purchase_tax_code_id: ['Purchase tax code', 'رمز ضريبة الشراء'],
        expense_account_id: ['Expense account', 'حساب المصروف'],
        'expense_account_id.help': ['A line on a vendor bill is booked to it.', 'يُقيَّد عليه سطر فاتورة المورد.'],
    };
    const out = {};
    for (const action of ['create', 'edit']) {
        for (const [key, [en, ar]] of Object.entries(words)) {
            const dot = key.indexOf('.');
            const field = dot < 0 ? key : key.slice(0, dot);
            const suffix = dot < 0 ? '' : key.slice(dot);
            out[`screen.invoicing.items.action.${action}.field.${field}${suffix}`] = locale === 'en' ? en : ar;
        }
    }
    return out;
}
//# sourceMappingURL=i18n-invoicing-sales.js.map